import { ADMINISTRATIVE_SCRIVENER } from "./administrative-scrivener.ts";
import { JUDICIAL_SCRIVENER } from "./judicial-scrivener.ts";
import { LABOR_CONSULTANT } from "./labor-consultant.ts";
import { LAWYER } from "./lawyer.ts";
import { STORE } from "./store.ts";
import { TAX_ACCOUNTANT } from "./tax-accountant.ts";
import { WEB_AGENCY } from "./web-agency.ts";
import type { ExpressionRule, ProfessionPack, Severity } from "./types.ts";

export type { ExpressionRule, ProfessionPack, SeasonalEvent, Severity } from "./types.ts";

export type { PackFeatures, PeriodRules, ReviewRules } from "./types.ts";

export const PROFESSIONS = { tax_accountant: TAX_ACCOUNTANT, lawyer: LAWYER, labor_consultant: LABOR_CONSULTANT, judicial_scrivener: JUDICIAL_SCRIVENER, administrative_scrivener: ADMINISTRATIVE_SCRIVENER, web_agency: WEB_AGENCY, store: STORE } as const satisfies Record<string, ProfessionPack>;
export type ProfessionId = keyof typeof PROFESSIONS;
export const PROFESSION_IDS = Object.keys(PROFESSIONS) as [ProfessionId, ...ProfessionId[]];

/** The 士業 an office can be registered as, for a select. */
export const PROFESSION_OPTIONS = PROFESSION_IDS.map((id) => ({ id, label: PROFESSIONS[id].label }));

/** The heading of a 知見帳 note in an article: 税理士の実務メモ / 弁護士の実務メモ. */
export const practiceNoteLabel = (pack: Pick<ProfessionPack, "label">) => `${pack.label}の実務メモ`;

export function profession(id: string): ProfessionPack {
  return (PROFESSIONS as Record<string, ProfessionPack>)[id] ?? TAX_ACCOUNTANT;
}

export interface ExpressionFinding {
  ruleId: string;
  severity: Severity;
  category: string;
  message: string;
  suggestion: string;
  match: string;
  index: number;
  /** Text around the match so the reviewer sees it in place. */
  context: string;
}

export function checkExpressions(text: string, rules: ExpressionRule[]): ExpressionFinding[] {
  const findings: ExpressionFinding[] = [];
  for (const rule of rules) {
    // Clone so a shared /g regex never carries lastIndex between calls.
    const re = new RegExp(rule.pattern.source, rule.pattern.flags.includes("g") ? rule.pattern.flags : `${rule.pattern.flags}g`);
    for (const m of text.matchAll(re)) {
      const index = m.index ?? 0;
      const start = Math.max(0, index - 24);
      const end = Math.min(text.length, index + m[0].length + 24);
      findings.push({
        ruleId: rule.id,
        severity: rule.severity,
        category: rule.category,
        message: rule.message,
        suggestion: rule.suggestion,
        match: m[0],
        index,
        context: `${start > 0 ? "…" : ""}${text.slice(start, end).replace(/\n/g, " ")}${end < text.length ? "…" : ""}`,
      });
    }
  }
  // Overlapping rules (e.g. 必ず and 必ず節税) report once, the more specific first.
  findings.sort((a, b) => a.index - b.index || b.match.length - a.match.length);
  return findings.filter((f, i, all) => i === 0 || !(all[i - 1]!.index <= f.index && f.index < all[i - 1]!.index + all[i - 1]!.match.length));
}
